/**
 * Dashboard Component
 * Main dashboard containing all investment tracking functionality
 */

import React, { useEffect, useState, useMemo } from 'react';
import InvestmentList from './investments/InvestmentList';
import InvestmentPieChart from './investments/PieChart';
import TasksList from './tasks/TasksList';
import HistoricalChart from './investments/HistoricalChart';
import SaxoBanner from './investments/SaxoBanner';
import { apiGet } from '../utils/api';

// Compute change vs N days ago using valueHistory across all investments
function computeChange(investments, days) {
  if (!investments || investments.length === 0) return null;
  const cutoff = new Date(Date.now() - days * 86400000);

  let pastTotal = 0;
  let currentTotal = 0;
  let hasPast = false;

  investments.forEach(inv => {
    currentTotal += inv.eurValue || 0;
    const history = inv.valueHistory || [];
    if (history.length === 0) return;
    const sorted = [...history].sort((a, b) => new Date(a.timeOfUpdate) - new Date(b.timeOfUpdate));
    const past = sorted.filter(h => new Date(h.timeOfUpdate) <= cutoff).slice(-1)[0];
    if (past) {
      pastTotal += past.eurValue || 0;
      hasPast = true;
    }
  });

  if (!hasPast || pastTotal === 0) return null;
  const pct = ((currentTotal - pastTotal) / pastTotal) * 100;
  const abs = currentTotal - pastTotal;
  return { pct, abs };
}

// Start-of-year total
function computeYTD(investments) {
  const jan1 = new Date(new Date().getFullYear(), 0, 1);
  return computeChange(investments.map(inv => ({
    ...inv,
    valueHistory: (inv.valueHistory || []).filter(h => new Date(h.timeOfUpdate) <= jan1)
      .concat(inv.valueHistory || [])
  })), Math.ceil((Date.now() - jan1.getTime()) / 86400000));
}

const Dashboard = () => {
  const [investments, setInvestments] = useState([]);
  const [totalEurValue, setTotalEurValue] = useState(0);
  const [typeTotals, setTypeTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [selectedTypes, setSelectedTypes] = useState([]);

  const allTypes = useMemo(
    () => [...new Set(investments.map((inv) => inv.type).filter(Boolean))],
    [investments]
  );

  useEffect(() => {
    if (allTypes.length > 0 && selectedTypes.length === 0) {
      setSelectedTypes(allTypes);
    }
  }, [allTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredInvestments = useMemo(() => {
    if (selectedTypes.length === 0) return investments;
    return investments.filter((inv) => selectedTypes.includes(inv.type));
  }, [investments, selectedTypes]);

  const change24h = useMemo(() => computeChange(investments, 1), [investments]);
  const changeYTD  = useMemo(() => {
    if (!investments.length) return null;
    const jan1 = new Date(new Date().getFullYear(), 0, 1);
    let pastTotal = 0;
    let hasPast = false;
    investments.forEach(inv => {
      const history = (inv.valueHistory || []).sort((a, b) => new Date(a.timeOfUpdate) - new Date(b.timeOfUpdate));
      const past = history.filter(h => new Date(h.timeOfUpdate) <= jan1).slice(-1)[0];
      if (past) { pastTotal += past.eurValue || 0; hasPast = true; }
    });
    if (!hasPast || pastTotal === 0) return null;
    const pct = ((totalEurValue - pastTotal) / pastTotal) * 100;
    const abs = totalEurValue - pastTotal;
    return { pct, abs };
  }, [investments, totalEurValue]);

  const fetchInvestments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiGet('/api/investments');
      const investmentsData = response.data || response;
      setInvestments(investmentsData);
      const total = investmentsData.reduce((sum, inv) => sum + (inv.eurValue || 0), 0);
      setTotalEurValue(total);
      const totals = investmentsData.reduce((acc, inv) => {
        if (inv.type) acc[inv.type] = (acc[inv.type] || 0) + (inv.eurValue || 0);
        return acc;
      }, {});
      setTypeTotals(totals);
    } catch (err) {
      console.error('Error fetching investments:', err);
      setError('Failed to load investments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInvestments(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading your investments...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <div className="error-message">
          <h3>Error Loading Data</h3>
          <p>{error}</p>
          <button onClick={fetchInvestments} className="retry-button">Try Again</button>
        </div>
      </div>
    );
  }

  const sortedTypeTotals = Object.entries(typeTotals).sort(([, a], [, b]) => b - a);

  return (
    <div className="dashboard">

      {/* ── Integration health banners ── */}
      <SaxoBanner />

      {/* ── Hero Stats Bar ── */}
      <section className="portfolio-hero">
        <div className="hero-main">
          <div className="hero-label">Total Portfolio Value</div>
          <div className="hero-total">€{Math.round(totalEurValue).toLocaleString()}</div>
        </div>

        <div className="hero-stats">
          {change24h !== null && (
            <div className={`hero-stat ${change24h.pct >= 0 ? 'hero-stat--up' : 'hero-stat--down'}`}>
              <div className="hero-stat-label">24h</div>
              <div className="hero-stat-value">
                {change24h.pct >= 0 ? '▲' : '▼'} {Math.abs(change24h.pct).toFixed(2)}%
              </div>
              <div className="hero-stat-abs">
                {change24h.abs >= 0 ? '+' : ''}€{Math.round(change24h.abs).toLocaleString()}
              </div>
            </div>
          )}

          {changeYTD !== null && (
            <div className={`hero-stat ${changeYTD.pct >= 0 ? 'hero-stat--up' : 'hero-stat--down'}`}>
              <div className="hero-stat-label">YTD</div>
              <div className="hero-stat-value">
                {changeYTD.pct >= 0 ? '▲' : '▼'} {Math.abs(changeYTD.pct).toFixed(2)}%
              </div>
              <div className="hero-stat-abs">
                {changeYTD.abs >= 0 ? '+' : ''}€{Math.round(changeYTD.abs).toLocaleString()}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── Investment Types Breakdown ── */}
      <section className="type-totals">
        <h3>Investment Types</h3>
        <div className="type-totals-container">
          <ul className="type-totals-list">
            {sortedTypeTotals.map(([type, total]) => (
              <li key={type}>
                <span className="type-name">{type}</span>
                <span className="type-value">€{Math.round(total).toLocaleString()}</span>
              </li>
            ))}
          </ul>
          <div className="type-totals-chart">
            <InvestmentPieChart data={typeTotals} />
          </div>
        </div>
      </section>

      {/* ── Portfolio History Chart ── */}
      <section className="historical-chart">
        <h3>Portfolio History</h3>
        <HistoricalChart
          investments={filteredInvestments}
          selectedTypes={selectedTypes}
        />
      </section>

      {/* ── Investment List ── */}
      <section className="investments-list">
        <h3>Investment Overview</h3>
        <InvestmentList
          investments={investments}
          setInvestments={setInvestments}
          onDataUpdate={fetchInvestments}
          selectedTypes={selectedTypes}
          setSelectedTypes={setSelectedTypes}
          allTypes={allTypes}
        />
      </section>

      {/* ── Tasks ── */}
      <section className="tasks-list">
        <h3>Tasks</h3>
        <TasksList />
      </section>
    </div>
  );
};

export default Dashboard;
